// Central SEO configuration — single source of truth for site-wide metadata
// and JSON-LD schemas. Imported anywhere we need consistent business info.

export const SITE = {
  url: 'https://www.harp-solutions.co.uk',
  name: 'H.A.R.P Solutions LTD',
  legalName: 'H.A.R.P Solutions LTD',
  tagline: 'Engineered for Reliability. Delivered Without Delay.',
  phone: '+44 330 122 1094',
  phoneTel: '+443301221094',
  whatsapp: '+443301221094', // reuse main phone until a dedicated WhatsApp Business number is provided
  email: 'info@harp-solutions.co.uk',
  logo: '/harp-favicon.png?v=5',
  // Registered business address
  address: {
    streetAddress: 'Suite RA01, 195-197 Wood Street',
    addressLocality: 'London',
    postalCode: 'E17 3NU',
    addressCountry: 'GB',
  },
  // Pre-formatted Google Maps URL for the address (used by Footer + ContactSection)
  mapUrl:
    'https://www.google.com/maps/search/?api=1&query=H.A.R.P+Solutions+LTD%2C+Suite+RA01%2C+195-197+Wood+Street%2C+London+E17+3NU',
  // Geographic coverage
  serviceAreas: ['London', 'Greater London', 'Essex', 'Kent'],
  // Human-readable service area line (used under the address)
  serviceAreaLine: 'Serving London, Greater London, Essex, Kent & Surrounding Areas',
  // ISO 8601 day codes for opening hours
  openingHours: 'Mo-Su 00:00-23:59', // 24/7 emergency
  // Social presence — fill these in as accounts go live
  sameAs: [
    // 'https://www.facebook.com/harpsolutions',
    // 'https://www.instagram.com/harpsolutions',
    // 'https://uk.linkedin.com/company/harp-solutions',
  ],
  // Lat/lng of the registered office (Wood Street, Walthamstow, London E17)
  geo: { lat: 51.5895, lng: -0.0098 },
};

// Schema.org LocalBusiness skeleton. Each page can extend this with @type Service.
export const buildLocalBusinessJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'Plumber', 'HomeAndConstructionBusiness'],
  '@id': `${SITE.url}/#business`,
  name: SITE.legalName,
  url: SITE.url,
  telephone: SITE.phone,
  email: SITE.email,
  image: `${SITE.url}${SITE.logo}`,
  logo: `${SITE.url}${SITE.logo}`,
  description:
    'Trusted plumbing, emergency plumber, handyman and property maintenance services across Kent, London and Essex. 24/7 callouts, fully insured engineers.',
  priceRange: '££',
  address: {
    '@type': 'PostalAddress',
    streetAddress: SITE.address.streetAddress,
    addressLocality: SITE.address.addressLocality,
    postalCode: SITE.address.postalCode,
    addressCountry: SITE.address.addressCountry,
  },
  areaServed: SITE.serviceAreas.map((a) => ({ '@type': 'AdministrativeArea', name: a })),
  openingHours: SITE.openingHours,
  sameAs: SITE.sameAs,
  geo: { '@type': 'GeoCoordinates', latitude: SITE.geo.lat, longitude: SITE.geo.lng },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: SITE.phone,
      contactType: 'Customer Service',
      areaServed: 'GB',
      availableLanguage: ['English'],
    },
    {
      '@type': 'ContactPoint',
      telephone: SITE.phone,
      contactType: 'Emergency',
      areaServed: 'GB',
      availableLanguage: ['English'],
      hoursAvailable: '24/7',
    },
  ],
});

// Schema.org Service entry — used on service pages
export const buildServiceJsonLd = ({ name, slug, description, areaServed = SITE.serviceAreas }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE.url}/services/${slug}/#service`,
  name,
  description,
  provider: { '@id': `${SITE.url}/#business` },
  areaServed: areaServed.map((a) => ({ '@type': 'AdministrativeArea', name: a })),
  serviceType: name,
  url: `${SITE.url}/services/${slug}`,
});

// FAQ schema
export const buildFaqJsonLd = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

// Breadcrumb schema
export const buildBreadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.label,
    item: it.url.startsWith('http') ? it.url : `${SITE.url}${it.url}`,
  })),
});
